/**
 * 会话生命周期：过期判定、续期、单飞、并发保护（#89098）
 *
 * ============================================================================
 * 为什么这个文件比「调一下 /auth/v1/token」复杂得多
 *
 * 我方刷新句柄的语义是：**每次成功续期都轮换，旧句柄立即失效，没有宽限期。**
 * 这一条把几个平时属于「优化」的东西变成了正确性必需项。
 *
 * 具体的失效场景（不是假想，是这套语义的必然推论）：
 *
 *   3 个并发请求（database ×2 + storage ×1）同时发现 token 过期，各自拿着
 *   句柄 A 去续期。服务端侧有单飞锁，所以只有一个真的轮换成 B，另外两个抢不到
 *   锁、沿用旧记录返回（句柄仍是 A）。三个响应回到浏览器后**都会写 localStorage，
 *   且顺序不确定**：如果 B 先落盘、A 后落盘覆盖，客户端最终持有 A，而服务端
 *   已经轮换到 B、A 已失效 —— 下次续期必然 401，会话锁死。
 *
 *   注意这是**永久损坏**：本地只有失效的 A，没有任何自愈路径，用户被强制登出
 *   且重新登录前做不了任何事。
 *
 * 结论：**服务端单飞不能替代客户端单飞**。两者防的是不同的东西 ——
 * 服务端防「重复轮换」，客户端防「响应乱序覆盖 localStorage」。必须两边都有。
 *
 * 因此本文件实现四层保护，每层针对一个具体的竞态：
 *
 *   1. 单飞（inflight）      —— 同一实例内的并发续期合并成一次
 *   2. commit guard          —— 写入前复核 storage，句柄被别人换过就丢弃自己的结果
 *                               （这是唯一能挡住**跨标签页**覆盖的机制）
 *   3. 登出 epoch            —— 防「登出 + 在途续期」把用户重新登入
 *   4. 失败冷却              —— 服务端故障时不让每个请求都去打 /token
 * ============================================================================
 */

import type { SessionStore } from './session-store.js';
import type { CloudAuthEvent, CloudSession } from './types.js';

/**
 * 提前多久就认为「该续期了」。
 *
 * 余量放在**比较时**而不是写入时：写入时减会把余量固化进 localStorage，
 * 之后想调都调不了（CloudBase 就是写入时减 30s，改不动）。
 *
 * 90 秒的取值理由：token 要经网络到服务端才被校验，「本地还差 3 秒过期」的
 * token 到服务端时很可能已经过期了。留足一个网络往返加时钟偏差的余量。
 */
const EXPIRY_MARGIN_MS = 90_000;

/**
 * 同一个句柄续期失败后的冷却时间。
 *
 * 没有冷却时的表现：服务端故障期间，进入过期窗口后**每一个**业务请求都会去打
 * 一次 /token，把故障放大成请求风暴。
 *
 * 按句柄索引而非按时间点索引：句柄一旦换新就绕过冷却立即重试 —— 换新说明
 * 上一次其实成功了，没理由继续冷却。
 */
const REFRESH_FAILURE_COOLDOWN_MS = 60_000;

/** 续期请求的执行体，由 AuthModule 注入（它才知道怎么发 HTTP）。 */
export type RefreshExecutor = (refreshToken: string) => Promise<CloudSession>;

/** 续期彻底失败（凭据已不可用）时通知 AuthModule 广播 SIGNED_OUT。 */
export type SessionEventEmitter = (event: CloudAuthEvent, session: CloudSession | null) => void;

/** 判定一个错误是否意味着「凭据已废，清掉重登」。 */
export type CredentialsRejected = (error: unknown) => boolean;

export interface SessionManagerOptions {
    store: SessionStore;
    refresh: RefreshExecutor;
    emit: SessionEventEmitter;
    isCredentialsRejected: CredentialsRejected;
    /** 可注入时钟，仅供测试。 */
    now?: () => number;
}

export class SessionManager {
    private readonly store: SessionStore;
    private readonly refresh: RefreshExecutor;
    private readonly emit: SessionEventEmitter;
    private readonly isCredentialsRejected: CredentialsRejected;
    private readonly now: () => number;

    /** 在途的续期。并发调用共享同一个 Promise（第 1 层保护）。 */
    private inflight: Promise<CloudSession | null> | null = null;

    /**
     * 登出计数（第 3 层保护）。
     *
     * 在任何 await **之前**同步自增，续期流程在写入前后各读一次；若期间发生过
     * 登出，就把刚拿到的新会话丢弃。否则「用户点了登出 + 一个在途续期」会让
     * 用户在登出之后被重新登入。
     */
    private signOutEpoch = 0;

    /** 上次续期失败的句柄与时刻（第 4 层保护）。 */
    private lastFailure: { token: string; at: number } | null = null;

    constructor(opts: SessionManagerOptions) {
        this.store = opts.store;
        this.refresh = opts.refresh;
        this.emit = opts.emit;
        this.isCredentialsRejected = opts.isCredentialsRejected;
        this.now = opts.now ?? Date.now;
    }

    /** 读当前会话（不触发续期）。总是从 storage 重读，见 session-store.ts。 */
    peek(): CloudSession | null {
        return this.store.read();
    }

    /**
     * 取一个**当前可用**的会话，必要时先续期。
     *
     * 这是 database / storage 请求路径上的入口，所以它必须「要么给一个能用的
     * token，要么给 null」，不能给一个即将过期的 token 让调用方去撞 401。
     */
    async ensure(): Promise<CloudSession | null> {
        const current = this.store.read();
        if (!current) {
            return null;
        }
        if (!this.isExpiring(current)) {
            return current;
        }
        if (!current.refreshToken) {
            // 没句柄就续不了。清掉：留着一个永远刷不动的会话，表现是每个请求
            // 都先白跑一次续期再失败。
            return this.abandon();
        }
        return this.refreshOnce(current);
    }

    /**
     * 强制续期一次，无论是否临近过期。
     *
     * 对外暴露给 `auth.refreshSession()` —— 应用有时需要在拿到「权限刚变更」
     * 的信号后立刻换一份新 token。
     */
    async forceRefresh(): Promise<CloudSession | null> {
        const current = this.store.read();
        if (!current?.refreshToken) {
            return null;
        }
        return this.refreshOnce(current, { ignoreCooldown: true });
    }

    /** 登录成功后落盘并广播。 */
    commitSignIn(session: CloudSession): CloudSession {
        this.lastFailure = null;
        this.store.write(session);
        this.emit('SIGNED_IN', session);
        return session;
    }

    /** 用户信息变更（如匿名升级重签了 token）后落盘并广播。 */
    commitUserUpdate(session: CloudSession): CloudSession {
        this.store.write(session);
        this.emit('USER_UPDATED', session);
        return session;
    }

    /**
     * 清掉本地会话并广播登出。
     *
     * 同步自增 epoch 是关键（见 signOutEpoch 注释）——必须在任何 await 之前。
     */
    clear(): void {
        this.signOutEpoch += 1;
        this.lastFailure = null;
        this.inflight = null;
        this.store.clear();
        this.emit('SIGNED_OUT', null);
    }

    /** 是否已进入「该续期」的窗口。 */
    private isExpiring(session: CloudSession): boolean {
        // 缺 expiresAt 一律当作已过期（fail-safe）：宁可多刷一次，也不要拿着
        // 一个不知道什么时候过期的 token 一直用下去。
        if (typeof session.expiresAt !== 'number') {
            return true;
        }
        return session.expiresAt - this.now() < EXPIRY_MARGIN_MS;
    }

    /** 单飞入口：在途则复用，否则起一次。 */
    private refreshOnce(
        current: CloudSession,
        opts: { ignoreCooldown?: boolean } = {},
    ): Promise<CloudSession | null> {
        if (this.inflight) {
            return this.inflight;
        }
        if (!opts.ignoreCooldown && this.inCooldown(current.refreshToken)) {
            // 冷却期内直接给出「没有可用会话」，不去打服务端。
            return Promise.resolve(null);
        }
        const task = this.doRefresh(current).finally(() => {
            this.inflight = null;
        });
        this.inflight = task;
        return task;
    }

    private inCooldown(refreshToken: string): boolean {
        const f = this.lastFailure;
        return (
            f !== null &&
            f.token === refreshToken &&
            this.now() - f.at < REFRESH_FAILURE_COOLDOWN_MS
        );
    }

    /** 实际续期，含 commit guard 与 epoch 复核。 */
    private async doRefresh(current: CloudSession): Promise<CloudSession | null> {
        const epochBefore = this.signOutEpoch;
        const handleBefore = current.refreshToken;

        let next: CloudSession;
        try {
            next = await this.refresh(handleBefore);
        } catch (err) {
            return this.onRefreshFailed(handleBefore, err);
        }

        // --- 第 3 层：期间是否发生过登出 -----------------------------------
        if (this.signOutEpoch !== epochBefore) {
            // 用户在续期途中登出了。把刚拿到的会话丢掉 —— 落盘就等于把用户
            // 重新登入，那是明确违背用户意图的。
            this.store.clear();
            return null;
        }

        // --- 第 2 层：commit guard ----------------------------------------
        // 复核 storage：若期间**别人**（另一个标签页）已经用同一个句柄换过一份
        // 新会话，那份才是服务端认的。此时写入自己的结果会把对方的覆盖掉，
        // 而被覆盖的那个句柄很可能已失效 —— 正是会话锁死的成因。
        const latest = this.store.read();
        if (latest && latest.refreshToken !== handleBefore) {
            return latest;
        }

        this.lastFailure = null;
        this.store.write(next);
        this.emit('TOKEN_REFRESHED', next);
        return next;
    }

    /**
     * 续期失败的分流。
     *
     * 「凭据被拒」与「网络/服务端故障」必须区别对待：
     *  - 凭据被拒（invalid_grant）→ 会话已不可能恢复，清掉并广播登出。留着它
     *    只会让每个后续请求都先白跑一次续期。
     *  - 其它（网络、5xx）→ **保留**会话，只记冷却。这类失败会自愈，清掉等于
     *    把一次网络抖动升级成用户被强制登出。
     */
    private onRefreshFailed(handle: string, err: unknown): CloudSession | null {
        if (this.isCredentialsRejected(err)) {
            return this.abandon();
        }
        this.lastFailure = { token: handle, at: this.now() };
        return null;
    }

    /** 会话已不可恢复：清本地并广播登出。 */
    private abandon(): null {
        this.store.clear();
        this.lastFailure = null;
        this.emit('SIGNED_OUT', null);
        return null;
    }
}
